import { useEffect, useRef, useState } from 'react';
import Select from 'ol/interaction/Select';
import Translate from 'ol/interaction/Translate';
import VectorLayer from 'ol/layer/Vector';
import { click } from 'ol/events/condition';
import type { Map as olMap } from 'ol';
import type { Tool } from '../contexts/@types/Map';


export function useTranslateFeatures(map: olMap | undefined, selectedTool: Tool | null) {
  const selectTranslateRef = useRef<Select | null>(null);
  const translateInteractionRef = useRef<Translate | null>(null);
  const [translateLayer, setTranslateLayer] = useState<VectorLayer<any> | null>(null);


  const enableTranslateInteraction = (layer: VectorLayer<any>) => {
    if (!map) return;

    if (selectTranslateRef.current) {
      try { map.removeInteraction(selectTranslateRef.current); } catch (e) {}
      selectTranslateRef.current = null;
    }
    if (translateInteractionRef.current) {
      try { map.removeInteraction(translateInteractionRef.current); } catch (e) {}
      translateInteractionRef.current = null;
    }

    const select = new Select({
      condition: click,
      layers: [layer],
    });

    const translate = new Translate({
      features: select.getFeatures(),
    });

    map.addInteraction(select);
    map.addInteraction(translate);

    selectTranslateRef.current = select;
    translateInteractionRef.current = translate;
  };

  const disableTranslateInteraction = () => {
    if (!map) return;
    if (selectTranslateRef.current) {
      try { map.removeInteraction(selectTranslateRef.current); } catch (e) {}
      selectTranslateRef.current = null;
    }
    if (translateInteractionRef.current) {
      try { map.removeInteraction(translateInteractionRef.current); } catch (e) {}
      translateInteractionRef.current = null;
    }
  };

  const handleSetTranslateLayer = (id: string) => {
    if (!map) return;

    if (id === 'disable') {
      setTranslateLayer(null);
      disableTranslateInteraction();
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find((l: any) => {
        const props = l.getProperties?.() ?? {};
        return (
          l instanceof VectorLayer &&
          (props.layertitle === id || props.uuid === id)
        );
      }) as VectorLayer<any> | undefined;

    if (!layer) return;

    setTranslateLayer(layer);
  };

  const isTranslateAction = (action?: unknown) => String(action) === 'translate' || String(action) === 'translateFeature';

  useEffect(() => {
    if (!map) return;

    if (isTranslateAction(selectedTool?.action) && translateLayer) {
      enableTranslateInteraction(translateLayer);
    } else {
      disableTranslateInteraction();
    }
    return () => {
      disableTranslateInteraction();
    };
  }, [map, selectedTool?.action, translateLayer]);

  useEffect(() => {
    if (!map) return;
    if (!isTranslateAction(selectedTool?.action)) {
      disableTranslateInteraction();
    }
  }, [selectedTool?.action, map]);

  return {
    translateLayer,
    handleSetTranslateLayer,
  };
}